import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Dropdown, DropdownMenu, DropdownToggle, Row } from "reactstrap";
import SimpleBar from "simplebar-react";
import { getToken, messaging, onMessage } from "firebase";
import { createNotificationSubscriber } from "utils/api";
import { VAPID_KEY } from "config";
import notificationIcon from "assets/images/svg/Notification.svg";
import msgIcon1 from "assets/images/svg/msg_icon-1.svg";
import { makeStyles } from "@material-ui/core/styles";
import CustomLink from "components/Common/buttons/CustomLink";
import { NOTIFICATION_MAP } from "components/CommonForBoth/TopbarDropdown/constants";
import { useSelector } from "react-redux";
import { LoginSelector } from "store/auth/login/selectors";
import { userHasOneOfRoles } from "utils/helpers/functions";
import { USER_ROLES } from "utils/constants";

const useStyles = makeStyles({
  badge: {
    zIndex: 9,
    color: "#FFF",
    fontSize: 12,
    borderRadius: "50%",
    backgroundColor: "#FF0000",
    position: "absolute",
    right: 3,
    top: 10,
    minWidth: 20,
    height: 20,
  },
  title: {
    padding: 20,
    borderBottom: "1px solid #0000000D",
    fontWeight: 600,
  },
  msgItem: {
    display: "flex",
    padding: 10,
    borderBottom: "1px solid #0000000D",

    "&> img": {
      width: 36,
      height: 36,
      marginRight: 10,
    },
  },
  actionText: {
    color: "#db6605",
    fontWeight: 600,
    marginLeft: 5,
  },
  noNotifications: {
    position: "fixed",
    textAlign: "center",
    top: "50%",
    width: "100%",
    color: "#777777",
  },
  wrapText: {
    wordBreak: "break-word",
  },
});
const NotificationDropdown = () => {
  const classes = useStyles();
  const [menu, setMenu] = useState(false);
  const [notifications, setNotifications] = useState([
    ...(localStorage.getItem("FBCM") ? JSON.parse(localStorage.getItem("FBCM")) : []),
  ]);
  const { user } = useSelector(LoginSelector);

  useEffect(() => {
    const safeRegisterMessaging = async () => {
      try {
        if (!("Notification" in window && "serviceWorker" in navigator)) return;
        await Notification.requestPermission();
        await new Promise(resolve => {
          if (document.readyState === "complete") return resolve();
          window.addEventListener("load", resolve, { once: true });
        });

        const registration = await navigator.serviceWorker
          .getRegistration("/firebase-messaging-sw.js")
          .then(async reg => {
            if (!reg) {
              return await navigator.serviceWorker.register("/firebase-messaging-sw.js");
            }
            return reg;
          });

        await navigator.serviceWorker.ready;

        const prevToken = localStorage.getItem("b2c:deviceToken");

        const currentToken = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration,
        });

        if (currentToken && currentToken !== prevToken) {
          await createNotificationSubscriber(currentToken);
          localStorage.setItem("b2c:deviceToken", currentToken);
        }

        onMessage(messaging, payload => {
          setNotifications(prev => {
            const updated = [{ ...payload.data, isNew: true }, ...prev];
            localStorage.setItem("FBCM", JSON.stringify(updated));
            return updated;
          });
        });
      } catch (error) {
        console.error("FCM ERROR:", error);
      }
    };

    user &&
      userHasOneOfRoles([
        USER_ROLES.SYSTEM_FULL_ADMINS,
        USER_ROLES.IT_OPERATIONS,
        USER_ROLES.IT_OPERATIONS_LEADERSHIP,
        USER_ROLES.CUSTOMER_CARE_LEADERSHIP,
        USER_ROLES.FINANCE,
      ]) &&
      safeRegisterMessaging();
  }, []);

  useEffect(() => {
    const bc = new BroadcastChannel("fcm_channel");

    bc.onmessage = payload => {
      setNotifications(prevState => {
        const updated = [{ ...payload.data.data, isNew: true }, ...prevState];
        localStorage.setItem("FBCM", JSON.stringify(updated));

        return updated;
      });
    };

    return () => {
      bc.close();
    };
  }, []);

  const newMsgCount = useMemo(() => notifications.filter(item => item.isNew).length || 0, [notifications]);
  const setAllRead = useCallback(() => {
    const newState = [
      ...notifications.map(item => {
        item.isNew = false;
        return item;
      }),
    ];
    localStorage.setItem("FBCM", JSON.stringify(newState));
    setNotifications(newState);
  }, [notifications]);

  if (
    !userHasOneOfRoles([
      USER_ROLES.SYSTEM_FULL_ADMINS,
      USER_ROLES.IT_OPERATIONS,
      USER_ROLES.IT_OPERATIONS_LEADERSHIP,
      USER_ROLES.CUSTOMER_CARE_LEADERSHIP,
      USER_ROLES.FINANCE,
    ])
  )
    return null;

  return (
    <>
      <Dropdown isOpen={menu} toggle={() => setMenu(!menu)} className="dropdown d-inline-block" tag="li">
        <DropdownToggle onClick={setAllRead} className="btn header-item " id="page-header-user-dropdown" tag="button">
          <img src={notificationIcon} alt="notification" />
          {newMsgCount > 0 && <span className={classes.badge}>{newMsgCount}</span>}
        </DropdownToggle>

        <DropdownMenu style={{ width: "350px" }} className="dropdown-menu dropdown-menu-lg p-0 dropdown-menu-end">
          <div className={classes.title}>
            <Row key="Notifications" className="align-items-center">
              <Col>
                <h6>Notifications</h6>
              </Col>
            </Row>
          </div>

          <SimpleBar style={{ height: notifications.length ? "450px" : "230px" }}>
            {notifications.length ? (
              notifications.map(notification => (
                <div className={classes.msgItem} key={`${notification.id}-${notification.timestamp}`}>
                  <img src={msgIcon1} alt="msgIcon" />
                  <div className={classes.wrapText}>
                    {NOTIFICATION_MAP[notification.action]?.text1 && (
                      <CustomLink color="#0085FF" underlined target="_blank" to={`/user/${notification.createdBy}`}>
                        {notification.createdBy}
                      </CustomLink>
                    )}
                    {`${NOTIFICATION_MAP[notification.action]?.text1}`}
                    <CustomLink
                      color="#0085FF"
                      underlined
                      target="_blank"
                      to={NOTIFICATION_MAP[notification.action]?.link(notification.id)}
                    >
                      {notification[NOTIFICATION_MAP[notification.action]?.key]}
                    </CustomLink>
                    <span className={classes.actionText}>{NOTIFICATION_MAP[notification.action]?.text2}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className={classes.noNotifications}>No notifications yet</div>
            )}
          </SimpleBar>
        </DropdownMenu>
      </Dropdown>
    </>
  );
};

export default NotificationDropdown;
